import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, unref as _unref, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "grouped-advance panel bg-lighter-grey" }
const _hoisted_2 = { class: "row justify-between" }
const _hoisted_3 = { class: "advance-title truncate-line" }
const _hoisted_4 = { class: "advance-summary margin-0" }
const _hoisted_5 = {
  key: 0,
  class: "text-body-light light-text"
}
const _hoisted_6 = {
  key: 1,
  class: "text-body-light light-text"
}
const _hoisted_7 = { class: "advance-total text-right" }
const _hoisted_8 = { class: "total-text text-body-light" }
const _hoisted_9 = { class: "total-amount" }
const _hoisted_10 = { class: "bar" }
const _hoisted_11 = { class: "row justify-between" }
const _hoisted_12 = { class: "recouped text-secondary" }
const _hoisted_13 = { class: "remaining text-right text-body-light" }
const _hoisted_14 = { class: "row no-space pad-0" }
const _hoisted_15 = { class: "issue-date col-6" }
const _hoisted_16 = { class: "text-body-light light-text" }
const _hoisted_17 = { class: "combined col-6" }
const _hoisted_18 = { class: "text-body-light light-text" }
const _hoisted_19 = { key: 0 }
const _hoisted_20 = { key: 1 }
const _hoisted_21 = { key: 2 }
const _hoisted_22 = {
  key: 0,
  class: "transactions-list no-bullets pad-0"
}

import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useGettext } from 'vue3-gettext'
import Loader from '@/components/CommonUI/Loader.vue'
import GroupedAdvanceTransaction from '@/components/GroupedAdvanceTransaction.vue'
import { formatAmount } from '@/helpers/formatAmount'
import { formatDate } from '@/helpers/formatDate'
import { useAdvanceStore } from '@/store'
import { AdvanceTypeNames, AdvanceType, ReleaseType } from '@/constants'


export default /*@__PURE__*/_defineComponent({
  __name: 'GroupedAdvance',
  props: {
    groupedAdvance: {}
  },
  setup(__props: any) {

const { $gettext, $ngettext } = useGettext()

const props = __props

const { getReleaseById } = useAdvanceStore()
const { advanceReleaseTitles, advanceAssetTitles } = storeToRefs(useAdvanceStore())

const isExpanded = ref(false)

const isAssetAdvance = computed(() => props.groupedAdvance.type === AdvanceType.asset)
const isReleaseAdvance = computed(() => props.groupedAdvance.type === AdvanceType.release)

const areTitlesLoading = computed(() => {
  if (isReleaseAdvance.value) {
    return props.groupedAdvance.entityIds.some(id => advanceReleaseTitles.value[id] === undefined)
  }

  if (isAssetAdvance.value) {
    return props.groupedAdvance.entityIds.some(id => advanceAssetTitles.value[id] === undefined)
  }

  return false
})
const isCancelled = computed(() => props.groupedAdvance.transactions.every(transaction => !!transaction.endsAt))
const isShared = computed(() => props.groupedAdvance.entityIds.length > 1)
const isSingleReleaseAdvance = computed(() => isReleaseAdvance.value && !isShared.value)
const releaseDetails = computed(() => getReleaseById(props.groupedAdvance.entityIds[0]))
const releaseTitle = computed(() => {
  if (!isReleaseAdvance.value || areTitlesLoading.value) return ''

  return props.groupedAdvance.entityIds
    .map(id => advanceReleaseTitles.value[id])
    .filter(Boolean)
    .join(', ')
})

const uniqueTrackTitles = computed(() => {
  if (!isAssetAdvance.value) return []

  return Array.from(new Set(props.groupedAdvance.entityIds
    .map(id => advanceAssetTitles.value[id])
    .filter(Boolean)))
})

const trackTitle = computed(() => uniqueTrackTitles.value.join(', '))

const isSharedDisplay = computed(() => {
  if (isAssetAdvance.value) {
    return uniqueTrackTitles.value.length > 1
  }

  return isShared.value
})

const entityTitle = computed(() => {
  if (props.groupedAdvance.type === AdvanceType.user) return $gettext('Account Advance')
  if (isAssetAdvance.value) return trackTitle.value

  return releaseTitle.value
})

const advanceType = computed(() => {
  if (isCancelled.value) return $gettext('Cancelled')
  if (isSharedDisplay.value && isReleaseAdvance.value) return $gettext('Shared Release')
  if (isSharedDisplay.value && isAssetAdvance.value) return $gettext('Shared Track')

  return $gettext(AdvanceTypeNames[props.groupedAdvance.type]) || $gettext('Advance')
})

const advanceTypeClass = computed(() => (
  isCancelled.value
    ? 'cancelled'
    : isSharedDisplay.value
      ? 'shared'
      : props.groupedAdvance.type
))
const advanceTotal = computed(() => props.groupedAdvance.transactions
  .reduce((total, transaction) => (total + parseFloat(transaction.amount)), 0))

const recoupedTotal = computed(() => props.groupedAdvance.transactions
  .reduce((total, transaction) => (total + parseFloat(transaction.bundledAmount)), 0))

const remainingTotal = computed(() => advanceTotal.value - recoupedTotal.value)
const percentageRecouped = computed(() => (
  remainingTotal.value === 0
    ? 100
    : parseFloat(((recoupedTotal.value / advanceTotal.value) * 100).toFixed(2))
))

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("article", _hoisted_1, [
    _createElementVNode("header", _hoisted_2, [
      _createElementVNode("div", null, [
        _createElementVNode("div", {
          class: _normalizeClass(["advance-type", advanceTypeClass.value])
        }, _toDisplayString(advanceType.value), 3),
        _createElementVNode("h3", _hoisted_3, [
          (areTitlesLoading.value)
            ? (_openBlock(), _createBlock(Loader, { key: 0 }))
            : _createCommentVNode("", true),
          _createTextVNode(" " + _toDisplayString(entityTitle.value), 1)
        ]),
        _createElementVNode("p", _hoisted_4, [
          (isSingleReleaseAdvance.value && releaseDetails.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_5, _toDisplayString(_unref(ReleaseType)[releaseDetails.value!.musicType]) + " • " + _toDisplayString(`${_unref($gettext)('Released')} ${_unref(formatDate)(releaseDetails.value!.releaseDate, 'MMMM YYYY')}`) + " • ", 1))
            : _createCommentVNode("", true),
          (__props.groupedAdvance.type === _unref(AdvanceType).user)
            ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(_unref($gettext)('Applied across all releases')) + " • ", 1))
            : _createCommentVNode("", true),
          _createElementVNode("button", {
            class: "text-button",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (isExpanded.value = !isExpanded.value))
          }, _toDisplayString(_unref($ngettext)(
              '%{transactionsNumber} advance',
              '%{transactionsNumber} advances combined',
              __props.groupedAdvance.transactions.length,
              { transactionsNumber: String(__props.groupedAdvance.transactions.length) })), 1)
        ])
      ]),
      _createElementVNode("div", _hoisted_7, [
        _createElementVNode("span", _hoisted_8, _toDisplayString(_unref($gettext)('Total Advance')), 1),
        _createElementVNode("strong", _hoisted_9, _toDisplayString(_unref(formatAmount)(advanceTotal.value, false)), 1)
      ])
    ]),
    _createElementVNode("figure", _hoisted_10, [
      _createElementVNode("div", {
        class: "bar-done",
        style: _normalizeStyle(`width: ${percentageRecouped.value}%;`)
      }, null, 4)
    ]),
    _createElementVNode("div", _hoisted_11, [
      _createElementVNode("strong", _hoisted_12, _toDisplayString(_unref(formatAmount)(recoupedTotal.value, false)) + " " + _toDisplayString(_unref($gettext)('recouped')) + " " + _toDisplayString(`(${percentageRecouped.value}%)`), 1),
      _createElementVNode("span", _hoisted_13, _toDisplayString(_unref(formatAmount)(remainingTotal.value, false)) + " " + _toDisplayString(_unref($gettext)('remaining')), 1)
    ]),
    _createElementVNode("footer", _hoisted_14, [
      _createElementVNode("div", _hoisted_15, [
        _createElementVNode("small", _hoisted_16, _toDisplayString(_unref($gettext)('First issued')), 1),
        _createElementVNode("strong", null, _toDisplayString(_unref(formatDate)(__props.groupedAdvance.issueDate)), 1)
      ]),
      _createElementVNode("div", _hoisted_17, [
        _createElementVNode("small", _hoisted_18, _toDisplayString(_unref($gettext)('Advances Combined')), 1),
        (isShared.value && isAssetAdvance.value)
          ? (_openBlock(), _createElementBlock("strong", _hoisted_19, _toDisplayString(_unref($gettext)('%{tracksNumber} tracks', { tracksNumber: String(__props.groupedAdvance.entityIds.length) })), 1))
          : (isShared.value)
            ? (_openBlock(), _createElementBlock("strong", _hoisted_20, _toDisplayString(_unref($gettext)('%{releasesNumber} releases', { releasesNumber: String(__props.groupedAdvance.entityIds.length) })), 1))
            : (_openBlock(), _createElementBlock("strong", _hoisted_21, _toDisplayString(_unref($ngettext)(
            '%{transactionsNumber} advance',
            '%{transactionsNumber} advances combined',
            __props.groupedAdvance.transactions.length,
            { transactionsNumber: String(__props.groupedAdvance.transactions.length) })), 1))
      ])
    ]),
    (isExpanded.value)
      ? (_openBlock(), _createElementBlock("ul", _hoisted_22, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.groupedAdvance.transactions, (transaction, index) => {
            return (_openBlock(), _createBlock(GroupedAdvanceTransaction, {
              key: `transaction-${transaction.id}`,
              "advance-number": index + 1,
              "advance-type": __props.groupedAdvance.type,
              transaction: transaction
            }, null, 8, ["advance-number", "advance-type", "transaction"]))
          }), 128))
        ]))
      : _createCommentVNode("", true)
  ]))
}
}

})